import { chain, zip } from 'lodash';
import {
  calculateExpandedGroupBounds,
  calculateMultiNodeBoundsWithPadding,
  convertChildPositionToAbsolute,
  convertChildPositionToRelative,
  getAdjacentPositionForNodeDuplication,
  getIntersectingNodes,
  rebaseChildPosition,
} from '@/components/Nodes/utils/nodeUtils';
import { CustomGroupData, DEFAULT_CUSTOM_GROUP_DATA, Node } from '@/types/node';
import { NodeType } from '@/enums/node-type.enum';
import { log } from '@/logger/logger';
import { NodeId } from '@/designer/designer';
import { track } from '@/utils/analytics';
import { NODE_GROUPING_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum } from '@/hooks/useAnalytics';
import { FlowGraph } from '../../FlowGraph';
import { UndoRedoEntry } from '../../undoRedo';
import { CustomGroupView } from '../CustomGroupView';
import { GroupBounds, DEFAULT_GROUP_PADDING } from './groups.types';
/**
 * Manages node grouping operations including creation, attachment, detachment,
 * and drag interactions with groups in the flow graph.
 */

const logger = log.getLogger('GroupsManagerView');
export class GroupsManagerView {
  constructor(private graph: FlowGraph) {}

  /** Shrink-wrap each group to fit its children, as a composable entry (no edit wrapper). */
  resizeGroupsToFitEntry(groupIds: NodeId[]): UndoRedoEntry {
    const entry = UndoRedoEntry.empty();
    for (const groupId of groupIds) {
      entry.add(new CustomGroupView(this.graph, groupId).resizeToFit());
    }
    return entry;
  }

  resizeGroupsToFit(groupIds: NodeId[]): void {
    if (groupIds.length === 0) return;
    this.graph.edit(() => this.resizeGroupsToFitEntry(groupIds), false);
  }

  /**
   * Creates a new group from currently selected nodes.
   * Selected nodes become children of the new group.
   * @param nodeIds - Optional array of node IDs to group. If not provided, uses currently selected nodes.
   */
  /** Returns whether a group was created. */
  createGroupFromSelectedNodes(nodeIds?: NodeId[]): boolean {
    const selectedNodesIds = nodeIds ?? this.graph.getSelectedNodes();
    const selectedNodes = selectedNodesIds.map((id) => this.graph.store.getNode(id));

    if (selectedNodes.length === 0) {
      return false;
    }

    // Don't allow grouping if any selected node is already a group
    if (selectedNodes.some((node) => node.type === NodeType.CustomGroup)) {
      // todo: should we merge groups to one if other groups are selected? Maybe a problem if group is customized already...
      return false;
    }

    this.graph.edit(() => {
      const groupNodeBounds = calculateMultiNodeBoundsWithPadding(selectedNodes, DEFAULT_GROUP_PADDING);
      const groupNodeData = this.createGroupNodeStructure(groupNodeBounds);

      // todo: send metric (as in Flow.jsx when adding a new node)
      const undoEntry = this.graph.addNodes([groupNodeData]);
      undoEntry.add(this.setNodesParentAndPosition(selectedNodes, groupNodeData.id, groupNodeBounds));
      this.graph.setSelectedNodes([groupNodeData.id]);

      return undoEntry;
    });
    track(NODE_GROUPING_EVENT_NAMES.CREATED, { nodeIds: selectedNodesIds }, TrackTypeEnum.Product);
    return true;
  }

  /**
   * Creates a group from the given node IDs with a custom name, returning the new group's
   * id and an UndoRedoEntry for use inside a parent edit() block. Does not auto-select.
   * Optional metadata overrides the corresponding fields on the new wrapper's data.
   */
  createGroupFromNodes(
    nodeIds: NodeId[],
    groupName: string,
    metadata?: { color?: string; labelFontSize?: number },
  ): { entry: UndoRedoEntry; groupId: NodeId } {
    if (nodeIds.length === 0) return { entry: UndoRedoEntry.empty(), groupId: '' };

    const nodes = nodeIds.map((id) => this.graph.store.getNode(id));
    const groupNodeBounds = calculateMultiNodeBoundsWithPadding(nodes, DEFAULT_GROUP_PADDING);
    const groupNodeData = this.createGroupNodeStructure(groupNodeBounds, { name: groupName, ...metadata });

    const entry = this.graph.addNodes([groupNodeData]);
    entry.add(this.setNodesParentAndPosition(nodes, groupNodeData.id, groupNodeBounds));

    return { entry, groupId: groupNodeData.id };
  }

  /**
   * Attaches nodes to an existing group when dropped over it.
   * Recalculates group bounds to accommodate new children and rebases existing children.
   */
  attachNodesToGroup(nodes: Node[], groupId: string): UndoRedoEntry {
    if (nodes.length === 0) {
      return UndoRedoEntry.empty();
    }

    const groupNode = this.graph.store.getNode(groupId);
    if (!groupNode) {
      logger.warn('NodeGroupingView: attachNodesToGroup: group node not found', {
        groupId,
      });
      return UndoRedoEntry.empty();
    }
    const existingChildren = this.graph.getChildNodesOfGroup(groupId);

    const newBounds = calculateExpandedGroupBounds(groupNode, existingChildren, nodes, DEFAULT_GROUP_PADDING);

    const undoEntry = this.updateGroupBounds(groupId, newBounds);
    undoEntry.add(this.setNodesParentAndPosition(nodes, groupId, newBounds));
    undoEntry.add(this.rebaseExistingChildren(existingChildren, groupNode.position, newBounds));
    track(
      NODE_GROUPING_EVENT_NAMES.ATTACHED,
      {
        groupId,
        nodeIds: nodes.map((node) => node.id),
        nodeTypes: nodes.map((node) => node.type),
        numberOfNodes: nodes.length,
      },
      TrackTypeEnum.Product,
    );
    return undoEntry;
  }

  /**
   * Expands a group when a child's position or dimensions change, if needed.
   * Rebases sibling positions to maintain their absolute positions.
   * @param childNode - The child node being updated
   * @param isOngoingDrag - True during active drag, false after drag stop or other operations
   */
  expandGroupAndRebaseChildrenIfNeeded(childNode: Node, isOngoingDrag: boolean = false): UndoRedoEntry {
    if (!childNode.parentId) {
      return UndoRedoEntry.empty();
    }

    const groupNode = this.graph.store.getNode(childNode.parentId);
    const siblingNodes = this.graph.getChildNodesOfGroup(childNode.parentId).filter((n) => n.id !== childNode.id);

    // Check if the node has positionAbsolute (from ReactFlow during drag operations)
    const nodeWithPositionAbsolute = childNode as Node & { positionAbsolute?: { x: number; y: number } };

    // Use positionAbsolute ONLY during active drag operations when it reflects real-time drag position
    // For other operations, positionAbsolute may be stale
    const childNodeAbsolute = {
      ...childNode,
      position:
        isOngoingDrag && nodeWithPositionAbsolute.positionAbsolute
          ? nodeWithPositionAbsolute.positionAbsolute
          : convertChildPositionToAbsolute(childNode, groupNode.position).position,
    };

    // For siblings, convert their relative positions to absolute using current group position
    const siblingNodesAbsolute = siblingNodes.map((sibling) =>
      convertChildPositionToAbsolute(sibling, groupNode.position),
    );

    const allChildrenAbsolute = [childNodeAbsolute, ...siblingNodesAbsolute];

    // Calculate new group bounds using absolute positions
    const newGroupBounds = calculateExpandedGroupBounds(groupNode, [], allChildrenAbsolute, DEFAULT_GROUP_PADDING);

    // Apply all updates in one transaction
    const undoEntry = this.updateGroupBounds(groupNode.id, newGroupBounds);

    // Update all children to new relative position within expanded bounds
    undoEntry.add(
      this.graph.store.setNodePositions(
        allChildrenAbsolute.map((node) => ({
          nodeId: node.id,
          position: convertChildPositionToRelative(node, newGroupBounds),
        })),
      ),
    );

    return undoEntry;
  }

  /**
   * Finds a group node that intersects with the given nodes.
   * Returns null if any of the nodes already have a parent or are groups themselves.
   */
  findIntersectingGroup(nodes: Node[]): string | null {
    if (nodes.length === 0) {
      return null;
    }

    if (nodes.some((node) => node.parentId || node.type === NodeType.CustomGroup)) {
      return null;
    }

    const groupNodes = this.graph.store.getNodes().filter((n) => n.type === NodeType.CustomGroup);
    const intersectingGroupNodes = nodes.flatMap((node) => getIntersectingNodes(node, groupNodes));

    // return the last intersecting group node (stacked highest)
    return intersectingGroupNodes.length > 0 ? intersectingGroupNodes[intersectingGroupNodes.length - 1].id : null;
  }

  /**
   * Finds which stationary nodes each group currently intersects with.
   * Returns an array where each entry represents a group and the nodes that partially intersects with it.
   *
   * @param groupNodes - Array of group nodes
   */
  findUngroupedNodesIntersectingWithGroups(groupNodes: Node[]): Array<{ groupId: string; nodes: Node[] }> {
    const allNodes = this.graph.store.getNodes();
    const ungroupedNonGroupNodes = allNodes.filter((node) => !node.parentId && node.type !== NodeType.CustomGroup);

    return groupNodes
      .map((groupNode) => ({
        groupId: groupNode.id,
        nodes: getIntersectingNodes(groupNode, ungroupedNonGroupNodes),
      }))
      .filter((attachment) => attachment.nodes.length > 0);
  }

  /**
   * Checks if all nodes share the same parent ID (all must have a parent)
   * Returns false if any node has no parent or if parents differ
   */
  areAllNodesInSameGroup(nodes: Node[]): boolean {
    const parentIds = chain(nodes)
      .map((node) => node.parentId)
      .compact()
      .value();
    return parentIds.length === nodes.length && new Set(parentIds).size === 1;
  }

  /**
   * Handles removal of nodes from their parent group when modifier key is used
   */
  removeNodesFromGroup(nodes: Node[]): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();

    for (const node of nodes) {
      const parentGroup = this.graph.store.getNode(node.parentId!);
      const { position } = convertChildPositionToAbsolute(node, parentGroup.position);

      undoEntry.add(this.graph.setNodeParent(node.id, undefined));
      undoEntry.add(this.graph.store.setNodePosition(node.id, position));
      track(
        NODE_GROUPING_EVENT_NAMES.DETACHED,
        { groupId: node.parentId!, nodeId: node.id, nodeType: node.type },
        TrackTypeEnum.Product,
      );
    }

    return undoEntry;
  }

  positionDuplicatedChildrenInExistingGroups(allDuplicatedNodes: Node[], newNodeIds: NodeId[]): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    const duplicatedNodeIdsSet = new Set(allDuplicatedNodes.map((node) => node.id));
    for (const [origNode, origNodeId, newNodeId] of zip(
      allDuplicatedNodes,
      allDuplicatedNodes.map((node) => node.id),
      newNodeIds,
    )) {
      if (!origNode || !origNodeId || !newNodeId) {
        continue;
      }

      if (!origNode.parentId) {
        continue;
      }

      if (duplicatedNodeIdsSet.has(origNode.parentId)) {
        continue;
      }

      // position the node adjacent to the original child node
      undoEntry.add(this.graph.store.setNodePosition(newNodeId, getAdjacentPositionForNodeDuplication(origNode)));
      undoEntry.add(this.expandGroupAndRebaseChildrenIfNeeded(this.graph.store.getNode(newNodeId)));
    }
    return undoEntry;
  }

  trackDeletedGroupNodes(deletedNodes: Node[]) {
    deletedNodes
      .filter((node) => {
        return node.type === NodeType.CustomGroup;
      })
      .forEach((groupNode) => {
        track(NODE_GROUPING_EVENT_NAMES.DELETED, { groupId: groupNode.id }, TrackTypeEnum.Product);
      });
  }

  private getIntersectingNodes(node: Node, candidateNodes: Node[]): Node[] {
    return candidateNodes.filter((candidate) => {
      const candidateLeft = candidate.position.x;
      const candidateTop = candidate.position.y;
      const candidateRight = candidate.position.x + (candidate.width ?? 0);
      const candidateBottom = candidate.position.y + (candidate.height ?? 0);

      const nodeLeft = node.position.x;
      const nodeTop = node.position.y;
      const nodeRight = node.position.x + (node.width ?? 0);
      const nodeBottom = node.position.y + (node.height ?? 0);

      return (
        candidateRight > nodeLeft && candidateLeft < nodeRight && candidateBottom > nodeTop && candidateTop < nodeBottom
      );
    });
  }

  /**
   * Sets parent relationship for nodes and converts their positions to relative coordinates
   */
  private setNodesParentAndPosition(nodes: Node[], groupId: NodeId, groupBounds: GroupBounds): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    for (const node of nodes) {
      const position = convertChildPositionToRelative(node, groupBounds);
      undoEntry.add(this.graph.store.setNodePosition(node.id, position));
      undoEntry.add(this.graph.setNodeParent(node.id, groupId));
    }
    return undoEntry;
  }

  /**
   * Updates existing children positions after group bounds change
   */
  private rebaseExistingChildren(
    children: Node[],
    oldParentPosition: { x: number; y: number },
    newBounds: GroupBounds,
  ): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    for (const child of children) {
      const position = rebaseChildPosition(child, oldParentPosition, newBounds);
      undoEntry.add(this.graph.store.setNodePosition(child.id, position));
    }
    return undoEntry;
  }

  /**
   * Updates a group node's bounds (position and size)
   */
  private updateGroupBounds(groupId: string, bounds: GroupBounds): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    undoEntry.add(
      this.graph.store.updateNode(groupId, () => ({
        position: { x: bounds.x, y: bounds.y },
        // TODO: we have to set these otherwise the onNodesChange handler in useReactFlowProps.ts overrides them - remove when possible
        width: bounds.width,
        height: bounds.height,
      })),
    );
    undoEntry.add(
      this.graph.store.updateNodeData<CustomGroupData>(groupId, () => ({
        width: bounds.width,
        height: bounds.height,
      })),
    );
    return undoEntry;
  }

  /**
   * Creates the data structure for a new group node. `overrides` lets callers
   * override fields on `data` (e.g. name, color, labelFontSize) — only keys
   * with defined values are applied, so `undefined` falls through to defaults.
   */
  private createGroupNodeStructure(bounds: GroupBounds, overrides?: Partial<CustomGroupData>): Node {
    const definedOverrides = Object.fromEntries(
      Object.entries(overrides ?? {}).filter(([, v]) => v !== undefined),
    ) as Partial<CustomGroupData>;
    return {
      id: crypto.randomUUID(),
      type: NodeType.CustomGroup,
      data: { ...DEFAULT_CUSTOM_GROUP_DATA, width: bounds.width, height: bounds.height, ...definedOverrides },
      position: { x: bounds.x, y: bounds.y },
      width: bounds.width,
      height: bounds.height,
      locked: false,
      parentId: undefined,
    };
  }
}
